import SelectBuscable from '@/shared/components/SelectBuscable'
import { estiloDeBoton } from '@/shared/lib/estilosDeBoton'
import { comoEventoDeCambio } from '@/shared/lib/opcionesDeSelect'
import { TituloDePantalla } from '@/shared/components/TituloDePantalla'
import { useState, useEffect, useRef } from 'react'
import { Checkbox } from '@/shared/components/Checkbox'
import { TextareaQueCrece } from '@/shared/components/TextareaQueCrece'
import { useNavigate, useLocation } from 'react-router-dom'
import { ArrowLeft, Save, AlertCircle } from 'lucide-react'
import { apiService } from '@/services/api'
import type { CreateJobRequestData } from '@/services/api'
import { useAuth } from '@/shared/hooks/useAuth'
import { getAreasCatalog, type Area } from '@/features/configuracion/api/areaService'
import { getProjectsPublicAll, type Project } from '@/features/configuracion/api/projectService'
import { hasAnyRole, userHasPermission } from '@/shared/lib/roleUtils'
import {
  avisoDePuestos,
  departamentoDeLaSolicitud,
  textoDelAviso,
} from '@/features/rh/lib/puestosDelDepartamento'
import RichTextEditor from '@/shared/components/RichTextEditor'
import { config } from '@/config/env'
import {
  MAXIMO_DE_VACANTES,
  MINIMO_DE_VACANTES,
  avisoNativoDeVacantes,
  errorDeVacantes,
} from '@/features/rh/lib/numeroDeVacantes'
import {
  // MAXIMO_DEL_CENTRO_DE_COSTO, // vuelve junto con el campo de Centro de costo
  MAXIMO_DEL_PORCENTAJE_DE_VIAJE,
  avisoDelCentroDeCosto,
  avisoDelPorcentajeDeViaje,
} from '@/features/rh/lib/centroDeCostoYViaje'
import { CandidatosInternos } from '@/features/rh/components/CandidatosInternos'
import {
  avisoDeCandidatosInternos,
  filasQueSeMandan,
} from '@/features/rh/lib/candidatosInternos'
import {
  MOTIVOS_DE_SOLICITUD,
  NIVELES_DE_ESCOLARIDAD,
  NIVELES_DE_INGLES,
  TIPOS_DE_CONTRATO,
} from '@/features/rh/lib/catalogosDeRequisicion'
import { PaqueteriaRequerida } from '@/features/rh/components/PaqueteriaRequerida'
import { esHtmlVacio, habilidadesAlElegirPuesto } from '@/features/rh/lib/descripcionDelPuesto'
import {
  CLAVE_DE_DUPLICADO,
  type DatosDeSolicitudDuplicada,
} from '@/features/rh/lib/duplicarSolicitud'

const CreateJobRequest = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const backPath = location.pathname.startsWith('/job-requests-rh') ? '/job-requests-rh' : '/job-requests'
  const { user } = useAuth()
  /**
   * Si se llegó con «Duplicar» (SRH-318), el formulario abre ya llenado con la
   * otra solicitud. Nada se crea todavía: nace al enviarlo, con ID propio.
   */
  const duplicado = (location.state as Record<string, unknown> | null)?.[
    CLAVE_DE_DUPLICADO
  ] as DatosDeSolicitudDuplicada | undefined
  const [areas, setAreas] = useState<Area[]>([])
  const [isLoadingAreas, setIsLoadingAreas] = useState(false)
  const [projects, setProjects] = useState<Project[]>([])
  const [isLoadingProjects, setIsLoadingProjects] = useState(false)
  const [puestos, setPuestos] = useState<any[]>([])
  // Lo último que se puso solo en Habilidades mínimas, para saber si la persona
  // lo ha tocado desde entonces.
  const habilidadesAutollenadas = useRef('')
  const [isLoadingPuestos, setIsLoadingPuestos] = useState(false)
  // De qué departamento es la lista que hay en pantalla: mientras no coincida
  // con el de la solicitud, todavía no se sabe si tiene puestos.
  const [puestosDe, setPuestosDe] = useState<number | undefined>(undefined)
  
  const [formData, setFormData] = useState<CreateJobRequestData>({
    puestoSolicitar: '',
    areaVacante: '',
    sede: '',
    tipoPerfil: 'operativo',
    tipoContrato: 'tiempo_completo', // ⚠️ RENOMBRADO: 30 Oct 2025 - jornada → tipoContrato
    habilidadesMinimas: '',
    motivo: 'nueva',
    previousEmployeeName: '',
    costCenter: '',
    travelPercentage: undefined,
    fechaRequeridaInicio: '',
    prioridad: 'media',
    entrevistaFinalSolicitante: false,
    requiresHardwareSoftware: false,
    requiresWorkstation: false,
    numeroPlazas: 1,
    salarioMinimo: undefined,
    salarioMaximo: undefined,
    prestaciones: '',
    justificacionNecesidad: '', // ⚠️ AHORA OBLIGATORIO: 30 Oct 2025
    otraInformacion: '',
    proyectoId: '',
    // ⭐ NUEVOS CAMPOS OBLIGATORIOS: 30 Oct 2025
    gradoAcademico: 'licenciatura',
    añosExperiencia: undefined,
    // ⭐ NUEVOS CAMPOS OPCIONALES: 30 Oct 2025
    certificacionesRequeridas: [],
    otrasCertificaciones: '',
    englishLevel: null,
    requiredSoftware: [],
    internalCandidates: [],
    // Lo copiado va al final para pisar los valores de arranque. Lo que no se
    // copia —fecha, compensación, candidatos, a quién se reemplaza— se queda
    // vacío a propósito: son datos de la plaza original.
    ...duplicado?.datos,
  })

  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})
  const [showValidationAlert, setShowValidationAlert] = useState(false)

  // 🔄 ACTUALIZADO: 29 Oct 2025
  // Determinar si el usuario es ADMIN_RH o administrador
  const isAdminOrRH = hasAnyRole(user, ['MASTER'])
  // Un contrato por proyecto sin proyecto no dice por cuál es.
  const proyectoEsObligatorio = formData.tipoContrato === 'por_proyecto'

  // Quien puede crear solicitudes de cualquier área elige el departamento; el
  // resto crea en el suyo. La lista de puestos sigue al departamento de la
  // solicitud: un puesto solo se puede pedir del departamento que lo tiene.
  const puedeElegirDepartamento =
    isAdminOrRH ||
    userHasPermission('generales.catalogos.areas.read', user?.rbac?.effectivePermissions || []) ||
    userHasPermission('generales.catalogos.areas.update', user?.rbac?.effectivePermissions || [])
  const selectorDeAreaVisible =
    puedeElegirDepartamento &&
    !(formData.tipoPerfil === 'tecnico' && formData.tipoContrato === 'por_proyecto')
  const departamentoDeLaSolicitudId = departamentoDeLaSolicitud({
    selectorVisible: selectorDeAreaVisible,
    areaElegida: formData.areaId,
    areaPropia: user?.areaId,
  })
  const avisoDeLosPuestos = avisoDePuestos({
    departamento: departamentoDeLaSolicitudId,
    selectorVisible: selectorDeAreaVisible,
    cargando:
      isLoadingPuestos ||
      (Boolean(departamentoDeLaSolicitudId) && puestosDe !== departamentoDeLaSolicitudId),
    cantidad: puestos.length,
  })
  
  // Cargar áreas si es admin o RH
  useEffect(() => {
    if (isAdminOrRH) {
      loadAreas()
    }
    loadProjects()
  }, [isAdminOrRH])

  // Quien puede elegir departamento arranca con el suyo ya puesto.
  useEffect(() => {
    if (puedeElegirDepartamento && user?.areaId && !formData.areaId) {
      setFormData((prev) => (prev.areaId ? prev : { ...prev, areaId: user.areaId }))
    }
    // Solo al conocer al usuario: después manda lo que se elija.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [puedeElegirDepartamento, user?.areaId])

  useEffect(() => {
    loadPuestos(departamentoDeLaSolicitudId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [departamentoDeLaSolicitudId, selectorDeAreaVisible])

  const loadAreas = async () => {
    setIsLoadingAreas(true)
    try {
      const areasData = await getAreasCatalog()
      const activeAreas = areasData.filter(area => area.activa)
      setAreas(activeAreas)
    } catch (errCrudo: unknown) {
      const err = errCrudo as {
        message?: string;
        statusCode?: number;
        status?: number;
        error?: string;
        response?: { status?: number; data?: { message?: string } };
      };
      console.error('Error loading areas:', err)
    } finally {
      setIsLoadingAreas(false)
    }
  }

  const loadProjects = async () => {
    setIsLoadingProjects(true)
    try {
      const projectsData = await getProjectsPublicAll()
      setProjects(projectsData)
    } catch (errCrudo: unknown) {
      const err = errCrudo as {
        message?: string;
        statusCode?: number;
        status?: number;
        error?: string;
        response?: { status?: number; data?: { message?: string } };
      };
      console.error('Error loading projects:', err)
    } finally {
      setIsLoadingProjects(false)
    }
  }

  // Una respuesta lenta de un departamento anterior no debe pisar la lista del
  // que se eligió después.
  const peticionDePuestos = useRef(0)

  const loadPuestos = async (departamento?: number) => {
    const esta = ++peticionDePuestos.current
    if (!departamento) {
      setPuestos([])
      setIsLoadingPuestos(false)
      return
    }
    setIsLoadingPuestos(true)
    try {
      // El servidor toma el departamento del token; el parámetro solo lo
      // respeta para quien puede elegir uno.
      const consulta = selectorDeAreaVisible ? `?areaId=${departamento}` : ''
      const response = await fetch(
        `${config.apiBaseUrl}/catalogos/puestos/public/by-area${consulta}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('access_token')}`,
            'Content-Type': 'application/json',
          },
        },
      )
      if (!response.ok) throw new Error('No se pudieron cargar los puestos')
      const puestosData = await response.json()
      const puestosArray: any[] = Array.isArray(puestosData)
        ? puestosData
        : puestosData.data || []
      if (esta !== peticionDePuestos.current) return
      setPuestos(puestosArray)
      // Un puesto elegido (o copiado al duplicar) que no es de este
      // departamento se quita: el servidor lo rechazaría.
      setFormData((prev) =>
        prev.puestoSolicitar &&
        !puestosArray.some((p) => (p.nombre || p.name) === prev.puestoSolicitar)
          ? { ...prev, puestoSolicitar: '' }
          : prev,
      )
    } catch (errCrudo: unknown) {
      const err = errCrudo as { message?: string }
      console.error('Error loading puestos:', err)
      if (esta === peticionDePuestos.current) setPuestos([])
    } finally {
      if (esta === peticionDePuestos.current) {
        setIsLoadingPuestos(false)
        setPuestosDe(departamento)
      }
    }
  }

  /**
   * La descripción del puesto del catálogo pasa a Habilidades mínimas. Solo se
   * reemplaza lo que está vacío o lo que se puso solo con el puesto anterior:
   * si la persona ya escribió, no se le pisa.
   */
  const alElegirPuesto = (nombreDelPuesto: string) => {
    const puesto = puestos.find((p) => (p.nombre || p.name) === nombreDelPuesto)
    const nuevo = habilidadesAlElegirPuesto({
      actual: formData.habilidadesMinimas ?? '',
      ultimoAutollenado: habilidadesAutollenadas.current,
      descripcionDelPuesto: puesto?.descripcion,
    })
    if (nuevo === null) return

    habilidadesAutollenadas.current = nuevo
    setFormData((prev) => ({ ...prev, habilidadesMinimas: nuevo }))
    setFieldErrors((prev) => {
      const { habilidadesMinimas: _quitado, ...resto } = prev
      return resto
    })
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target
    // Limpiar error del campo cuando el usuario empiece a escribir
    if (fieldErrors[name]) {
      setFieldErrors(prev => {
        const newErrors = { ...prev }
        delete newErrors[name]
        return newErrors
      })
    }
    setShowValidationAlert(false)

    if (name === 'numeroPlazas' && e.target instanceof HTMLInputElement) {
      // El aviso del navegador queda en español desde que se escribe, no solo
      // cuando se intenta enviar: si no, `validationMessage` y el globo de
      // otro navegador salían en inglés («Value must be less than or equal
      // to 3»).
      e.target.setCustomValidity('')
      e.target.setCustomValidity(avisoNativoDeVacantes(e.target.validity))
    }

    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? (value === '' ? undefined : parseFloat(value)) : 
              type === 'checkbox' ? (e.target as HTMLInputElement).checked : value,
      // Al salir de Reemplazo se borra lo capturado. Si se quedara, el campo
      // desaparece de la pantalla pero el nombre sigue ahí y se mandaría: el
      // servidor lo descarta, pero volver a Reemplazo mostraría un valor viejo
      // que la persona ya había dejado atrás.
      ...(name === 'motivo' && value !== 'reemplazo'
        ? { previousEmployeeName: '' }
        : {}),
    }))
  }

  const validateForm = () => {
    const errors: Record<string, string> = {}
    
    // Validar campos requeridos
    if (!formData.puestoSolicitar?.trim()) {
      errors.puestoSolicitar = 'El puesto es obligatorio'
    }
    
    // Sin departamento no hay a qué solicitar el puesto. Quien elige lo ve en
    // su campo; quien crea en el suyo, como aviso general.
    if (!departamentoDeLaSolicitudId) {
      if (selectorDeAreaVisible) {
        errors.areaId = 'Debe seleccionar un Departamento/Área'
      } else {
        errors.general = 'Tu usuario no tiene un Departamento/Área asignada. Contacta al administrador.'
      }
    }
    
    // Campo areaVacante comentado - ya no es obligatorio
    // if (!formData.areaVacante?.trim()) {
    //   errors.areaVacante = 'El área es obligatoria'
    // }
    
    if (!formData.sede?.trim()) {
      errors.sede = 'La sede es obligatoria'
    }

    // Solo en Reemplazo: en los otros motivos el campo ni se muestra, y
    // exigirlo dejaría el formulario trabado sin nada visible que corregir.
    if (formData.motivo === 'reemplazo' && !formData.previousEmployeeName?.trim()) {
      errors.previousEmployeeName = 'Indica a quién se reemplaza'
    }

    // Los dos opcionales: en blanco no traba nada. Solo se revisa lo que
    // alcanza a escribirse de mas, para avisarlo junto al campo.
    const avisoCC = avisoDelCentroDeCosto(formData.costCenter)
    if (avisoCC) errors.costCenter = avisoCC

    const avisoViaje = avisoDelPorcentajeDeViaje(formData.travelPercentage)
    if (avisoViaje) errors.travelPercentage = avisoViaje

    const avisoCandidatos = avisoDeCandidatosInternos(
      formData.internalCandidates ?? [],
    )
    if (avisoCandidatos) errors.internalCandidates = avisoCandidatos
    
    // El editor vacío da <p></p>, no una cadena vacía: se mira el texto, no el HTML.
    if (esHtmlVacio(formData.habilidadesMinimas)) {
      errors.habilidadesMinimas = 'Las habilidades mínimas son obligatorias'
    }
    
    if (!formData.fechaRequeridaInicio) {
      errors.fechaRequeridaInicio = 'La fecha de inicio es obligatoria'
    } else {
      // Validar que la fecha sea hoy o futura
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      const selectedDate = new Date(formData.fechaRequeridaInicio)
      if (selectedDate < today) {
        errors.fechaRequeridaInicio = 'La fecha debe ser hoy o en el futuro'
      }
    }

    // Validar rango salarial
    if (formData.salarioMinimo !== undefined && formData.salarioMinimo < 0) {
      errors.salarioMinimo = 'El salario debe ser un valor positivo'
    }
    
    if (formData.salarioMaximo !== undefined && formData.salarioMaximo < 0) {
      errors.salarioMaximo = 'El salario debe ser un valor positivo'
    }
    
    if (formData.salarioMinimo && formData.salarioMaximo) {
      if (formData.salarioMinimo >= formData.salarioMaximo) {
        errors.salarioMinimo = 'El salario mínimo debe ser menor al máximo'
        errors.salarioMaximo = 'El salario máximo debe ser mayor al mínimo'
      }
    }

    // Validar número de vacantes
    const errorVacantes = errorDeVacantes(formData.numeroPlazas)
    if (errorVacantes) {
      errors.numeroPlazas = errorVacantes
    }

    // Un contrato por proyecto exige saber cuál.
    if (formData.tipoContrato === 'por_proyecto' && !formData.proyectoId) {
      errors.proyectoId = 'El proyecto es obligatorio cuando el contrato es por proyecto'
    }

    // ⭐ NUEVAS VALIDACIONES: 30 Oct 2025
    if (!formData.gradoAcademico) {
      errors.gradoAcademico = 'El grado académico es obligatorio'
    }

    if (formData.añosExperiencia === undefined || formData.añosExperiencia < 0) {
      errors.añosExperiencia = 'Los años de experiencia son obligatorios y deben ser positivos'
    }

    if (formData.añosExperiencia && formData?.añosExperiencia > 50) {
      errors.añosExperiencia = 'Los años de experiencia no pueden ser mayores a 50'
    }

    if (!formData.justificacionNecesidad?.trim()) {
      errors.justificacionNecesidad = 'La justificación es obligatoria'
    }

    setFieldErrors(errors)
    
    if (Object.keys(errors).length > 0) {
      setShowValidationAlert(true)
      setError('Por favor, corrija los errores en el formulario')
      // Hacer scroll al primer error
      const firstErrorField = Object.keys(errors)[0]
      const element = document.getElementById(firstErrorField)
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
      return false
    }

    return true
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (!validateForm()) {
      return
    }

    setIsLoading(true)

    try {
      // Preparar datos según el rol
      const dataToSend: any = { ...formData, areaId: departamentoDeLaSolicitudId }
      
      // Convertir certificacionesRequeridas de string a array
      if (dataToSend.certificacionesRequeridas && typeof dataToSend.certificacionesRequeridas === 'string') {
        dataToSend.certificacionesRequeridas = dataToSend.certificacionesRequeridas
          .split(',')
          .map((c: string) => c.trim())
          .filter((c: string) => c.length > 0)
      } else if (!dataToSend.certificacionesRequeridas) {
        dataToSend.certificacionesRequeridas = []
      }
      
      // El proyecto ya no depende del contrato: se manda si se eligió uno.
      if (!dataToSend.proyectoId) {
        delete dataToSend.proyectoId
      }

      // Una fila de paquetería que quedó con el nombre en blanco no se manda:
      // el backend la rechazaría con un 400 que no le dice nada a quien captura,
      // y lo que quiso decir es que ya no la quería.
      dataToSend.requiredSoftware = (formData.requiredSoftware ?? [])
        .map((fila) => ({ ...fila, name: fila.name.trim() }))
        .filter((fila) => fila.name !== '')

      // Misma idea con los candidatos internos: un renglon a medias o en
      // blanco es uno que ya no se queria, no un 400 que explicar.
      dataToSend.internalCandidates = filasQueSeMandan(
        formData.internalCandidates ?? [],
      )
      
      await apiService.createJobRequest(dataToSend)
      setSuccess(true)
      setTimeout(() => {
        navigate(backPath)
      }, 1500)
    } catch (errCrudo: unknown) {
      const err = errCrudo as {
        message?: string;
        statusCode?: number;
        status?: number;
        error?: string;
        response?: { status?: number; data?: { message?: string } };
      };
      setError(err.message || 'Error al crear la solicitud de vacante')
    } finally {
      setIsLoading(false)
    }
  }

  if (success) {
    return (
      <div className="w-full max-w-none px-4 sm:px-6 lg:px-8 py-6">
        <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
          <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Save className="w-6 h-6 text-green-600" />
          </div>
          <h3 className="text-lg font-medium text-green-900 mb-2">
            Solicitud Creada Exitosamente
          </h3>
          <p className="text-green-700">
            {/* Sin nombrar el puesto de quien valida: cuando el propio
                solicitante es el Project Manager, decia que estaba esperando
                a alguien que es el mismo que acababa de crearla. */}
            La solicitud de vacante ha sido creada y está pendiente de validación.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="w-full max-w-none my-8">
      {/* Header */}
      <div className="flex items-center mb-6">
        <button
          onClick={() => navigate(-1 as any)}
          className={estiloDeBoton('secundario')}
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Volver</span>
        </button>
        <TituloDePantalla nivel="interna" className="ml-6">
          Nueva solicitud de vacante
        </TituloDePantalla>
      </div>

      {/* SRH-318: de dónde viene lo que ya está llenado, y qué falta. */}
      {duplicado && (
        <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm font-medium text-blue-900">
            Duplicando la solicitud {duplicado.folioDeOrigen}
          </p>
          <p className="mt-1 text-sm text-blue-800">
            Copiamos el puesto, el perfil y los requisitos. Revísalos y captura
            la fecha de inicio. La compensación, los candidatos y el historial
            no se copian: la nueva solicitud empieza el flujo desde cero.
          </p>
        </div>
      )}

      {/* Error Alert */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertCircle className="w-5 h-5 text-red-500 mr-2" />
            <span className="text-red-700">{error}</span>
          </div>
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-md p-6">
        
        {/* Sección 1: Información Básica */}
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 border-b border-gray-200 pb-2">
            Información del usuario
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Puesto */}
            <div>
              <label htmlFor="puestoSolicitar" className="block text-sm font-medium text-gray-700 mb-2">
                Puesto *
              </label>
              {isLoadingPuestos ? (
                  <div className="flex items-center px-3 py-2 border border-gray-300 rounded-md bg-gray-50">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                    <span className="text-gray-600 text-sm">Cargando puestos...</span>
                  </div>
                ) : (
                  <SelectBuscable
                    variante="formulario"
                    id="puestoSolicitar"
                    name="puestoSolicitar"
                    value={formData.puestoSolicitar}
                    onChange={(valor) => {
                      handleChange(comoEventoDeCambio('puestoSolicitar', valor))
                      alElegirPuesto(valor)
                    }}
                    required
                    error={Boolean(fieldErrors.puestoSolicitar)}
                    placeholder="Selecciona un puesto"
                    placeholderDeBusqueda="Buscar puesto…"
                    opciones={puestos.map((puesto) => ({
                      value: puesto.nombre || puesto.name,
                      label: puesto.nombre || puesto.name,
                    }))}
                  />
                )}
              {avisoDeLosPuestos && (
                <p
                  className={`mt-1 text-sm flex items-center ${
                    avisoDeLosPuestos === 'elige_departamento' ? 'text-gray-500' : 'text-red-600'
                  }`}
                >
                  <AlertCircle className="w-4 h-4 mr-1" />
                  {textoDelAviso(avisoDeLosPuestos, selectorDeAreaVisible)}
                </p>
              )}
              {fieldErrors.puestoSolicitar && (
                <p className="mt-1 text-sm text-red-600 flex items-center">
                  <AlertCircle className="w-4 h-4 mr-1" />
                  {fieldErrors.puestoSolicitar}
                </p>
              )}
            </div>

            {/* Selector de Área para ADMIN/RH - Oculto si es Técnico y Por proyecto */}
            {selectorDeAreaVisible && (
              <div>
                <label htmlFor="areaId" className="block text-sm font-medium text-gray-700 mb-2">
                  Departamento/Área *
                </label>
                {isLoadingAreas ? (
                  <div className="flex items-center px-3 py-2 border border-gray-300 rounded-md bg-gray-50">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                    <span className="text-gray-600 text-sm">Cargando áreas...</span>
                  </div>
                ) : (
                  <>
                    <SelectBuscable
                      variante="formulario"
                      id="areaId"
                      name="areaId"
                      value={String(formData.areaId || '')}
                      onChange={(valor) => handleChange(comoEventoDeCambio('areaId', valor))}
                      required
                      error={Boolean(fieldErrors.areaId)}
                      placeholder="Selecciona un Departamento/Área"
                      placeholderDeBusqueda="Buscar área…"
                      opciones={areas.map((area) => ({
                        value: String(area.id),
                        label: `${area.nombre}${area.descripcion ? ` - ${area.descripcion}` : ''}`,
                      }))}
                    />
                    {fieldErrors.areaId && (
                      <p className="mt-1 text-sm text-red-600 flex items-center">
                        <AlertCircle className="w-4 h-4 mr-1" />
                        {fieldErrors.areaId}
                      </p>
                    )}
                  </>
                )}
              </div>
            )}

            {/* Sede */}
            <div>
              <label htmlFor="sede" className="block text-sm font-medium text-gray-700 mb-2">
                Sede *
              </label>
              <input
                type="text"
                id="sede"
                name="sede"
                value={formData.sede}
                onChange={handleChange}
                required
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 transition ${
                  fieldErrors.sede
                    ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                    : 'border-gray-300 focus:ring-blue-500 focus:border-transparent'
                }`}
                placeholder="Ej. Ciudad de México - Oficina Central"
              />
              {fieldErrors.sede && (
                <p className="mt-1 text-sm text-red-600 flex items-center">
                  <AlertCircle className="w-4 h-4 mr-1" />
                  {fieldErrors.sede}
                </p>
              )}
            </div>
          </div>



          {/* 🔄 ACTUALIZADO: 29 Oct 2025 - Error si PROJECT_MANAGER o SUPERVISOR sin área */}
          {!selectorDeAreaVisible && !user?.areaId && (
            <div className="mt-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex">
                  <AlertCircle className="w-5 h-5 text-red-500 mr-2 flex-shrink-0" />
                  <div>
                    <p className="text-sm text-red-700">
                      <strong>Error:</strong> Tu usuario no tiene un Departamento/Área asignada.
                    </p>
                    <p className="text-xs text-red-600 mt-1">
                      Contacta al administrador para que te asigne un área antes de crear solicitudes.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Sección 2: Características del Puesto */}
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 border-b border-gray-200 pb-2">
            Características del puesto
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Tipo de Perfil */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Tipo de puesto*
              </label>
              <div className="flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, tipoPerfil: 'operativo' }))}
                  className={`elevar px-6 py-3 rounded-lg font-medium transition-all ${
                    formData.tipoPerfil === 'operativo'
                      ? 'bg-[#253579] hover:bg-[#44528C] text-white shadow-md'
                      : 'bg-white text-gray-700 border border-gray-300 hover:border-blue-200 hover:bg-blue-50'
                  }`}
                >
                  Operativo
                </button>
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, tipoPerfil: 'tecnico' }))}
                  className={`elevar px-6 py-3 rounded-lg font-medium transition-all ${
                    formData.tipoPerfil === 'tecnico'
                      ? 'bg-[#253579] hover:bg-[#44528C] text-white shadow-md'
                      : 'bg-white text-gray-700 border border-gray-300 hover:border-blue-200 hover:bg-blue-50'
                  }`}
                >
                  Técnico
                </button>
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, tipoPerfil: 'administrativo' }))}
                  className={`elevar px-6 py-3 rounded-lg font-medium transition-all ${
                    formData.tipoPerfil === 'administrativo'
                      ? 'bg-[#253579] hover:bg-[#44528C] text-white shadow-md'
                      : 'bg-white text-gray-700 border border-gray-300 hover:border-blue-200 hover:bg-blue-50'
                  }`}
                >
                  Administrativo
                </button>
              </div>
            </div>

            {/* Tipo de Contrato (antes Jornada) */}
            <div>
              <label htmlFor="tipoContrato" className="block text-sm font-medium text-gray-700 mb-3">
                Tipo de Contrato *
              </label>
              <SelectBuscable
                variante="formulario"
                id="tipoContrato"
                name="tipoContrato"
                value={formData.tipoContrato}
                onChange={(valor) => handleChange(comoEventoDeCambio('tipoContrato', valor))}
                required
                opciones={TIPOS_DE_CONTRATO}
                placeholderDeBusqueda="Buscar tipo de contrato…"
              />
            </div>

            {/* Centro de costo: oculto por ahora, lo van a volver a poner.
                Para reactivarlo basta quitar este comentario; el estado, la
                validación y el envío siguen en su lugar.
            <div>
              <label htmlFor="costCenter" className="block text-sm font-medium text-gray-700 mb-3">
                Centro de costo
              </label>
              <input
                type="text"
                id="costCenter"
                name="costCenter"
                value={formData.costCenter ?? ''}
                onChange={handleChange}
                maxLength={MAXIMO_DEL_CENTRO_DE_COSTO}
                placeholder="ej. CC-001"
                aria-invalid={Boolean(fieldErrors.costCenter)}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 transition ${
                  fieldErrors.costCenter
                    ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                    : 'border-gray-300 focus:ring-blue-500 focus:border-transparent'
                }`}
              />
              {fieldErrors.costCenter && (
                <p className="mt-1 text-sm text-red-600 flex items-center">
                  <AlertCircle className="w-4 h-4 mr-1" />
                  {fieldErrors.costCenter}
                </p>
              )}
            </div>
            */}

            {/* Porcentaje de Viaje */}
            <div>
              <label htmlFor="travelPercentage" className="block text-sm font-medium text-gray-700 mb-3">
                Porcentaje de viaje
              </label>
              <input
                type="number"
                id="travelPercentage"
                name="travelPercentage"
                value={formData.travelPercentage ?? ''}
                onChange={handleChange}
                min={0}
                max={MAXIMO_DEL_PORCENTAJE_DE_VIAJE}
                step={1}
                placeholder="ej. 25"
                aria-invalid={Boolean(fieldErrors.travelPercentage)}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 transition ${
                  fieldErrors.travelPercentage
                    ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                    : 'border-gray-300 focus:ring-blue-500 focus:border-transparent'
                }`}
              />
              {fieldErrors.travelPercentage && (
                <p className="mt-1 text-sm text-red-600 flex items-center">
                  <AlertCircle className="w-4 h-4 mr-1" />
                  {fieldErrors.travelPercentage}
                </p>
              )}
            </div>

            {/* Proyecto / Centro de costos. Siempre visible. Es obligatorio solo
                cuando el contrato es por proyecto; en los demás casos se puede
                dejar en blanco. */}
            <div>
              <label htmlFor="proyectoId" className="block text-sm font-medium text-gray-700 mb-3">
                Proyecto/Centro de costos{proyectoEsObligatorio ? ' *' : ''}
              </label>
              {isLoadingProjects ? (
                <div className="flex items-center px-3 py-2 border border-gray-300 rounded-md bg-gray-50">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                  <span className="text-gray-600 text-sm">Cargando proyectos...</span>
                </div>
              ) : (
                <>
                  <SelectBuscable
                    variante="formulario"
                    id="proyectoId"
                    name="proyectoId"
                    value={formData.proyectoId || ''}
                    onChange={(valor) => {
                      setFormData(prev => ({ ...prev, proyectoId: valor }))
                      if (fieldErrors.proyectoId) {
                        setFieldErrors(prev => {
                          const newErrors = { ...prev }
                          delete newErrors.proyectoId
                          return newErrors
                        })
                      }
                    }}
                    required={proyectoEsObligatorio}
                    error={Boolean(fieldErrors.proyectoId)}
                    placeholder="Selecciona un proyecto"
                    placeholderDeBusqueda="Buscar proyecto…"
                    opciones={[
                      ...(proyectoEsObligatorio ? [] : [{ value: '', label: 'Sin proyecto' }]),
                      ...projects.map((project) => ({ value: project.id, label: project.name })),
                    ]}
                  />
                  {fieldErrors.proyectoId && (
                    <p className="mt-1 text-sm text-red-600 flex items-center">
                      <AlertCircle className="w-4 h-4 mr-1" />
                      {fieldErrors.proyectoId}
                    </p>
                  )}
                </>
              )}
            </div>
          </div>

        </div>

        {/* Sección 3: Perfil y Requisitos */}
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 border-b border-gray-200 pb-2">
            Perfil y Requisitos
          </h2>
          
          {/* Habilidades Mínimas */}
          <div>
            <label htmlFor="habilidadesMinimas" className="block text-sm font-medium text-gray-700 mb-2">
              Habilidades Mínimas *
            </label>
            <RichTextEditor
              value={formData.habilidadesMinimas}
              onChange={(value) => {
                setFormData(prev => ({ ...prev, habilidadesMinimas: value }))
                if (fieldErrors.habilidadesMinimas) {
                  setFieldErrors(prev => {
                    const newErrors = { ...prev }
                    delete newErrors.habilidadesMinimas
                    return newErrors
                  })
                }
              }}
              placeholder="Describe las habilidades y requisitos mínimos necesarios para el puesto..."
              hasError={!!fieldErrors.habilidadesMinimas}
            />
            {fieldErrors.habilidadesMinimas && (
              <p className="mt-1 text-sm text-red-600 flex items-center">
                <AlertCircle className="w-4 h-4 mr-1" />
                {fieldErrors.habilidadesMinimas}
              </p>
            )}
          </div>

          {/* ⭐ NUEVOS CAMPOS: 30 Oct 2025 - Requisitos académicos y profesionales */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
            {/* Grado Académico Requerido */}
            <div>
              <label htmlFor="gradoAcademico" className="block text-sm font-medium text-gray-700 mb-2">
                Escolaridad requerida *
              </label>
              <SelectBuscable
                variante="formulario"
                id="gradoAcademico"
                name="gradoAcademico"
                value={formData.gradoAcademico}
                onChange={(valor) => handleChange(comoEventoDeCambio('gradoAcademico', valor))}
                required
                error={Boolean(fieldErrors.gradoAcademico)}
                placeholder="Seleccione..."
                placeholderDeBusqueda="Buscar escolaridad…"
                opciones={NIVELES_DE_ESCOLARIDAD}
              />
              {fieldErrors.gradoAcademico && (
                <p className="mt-1 text-sm text-red-600 flex items-center">
                  <AlertCircle className="w-4 h-4 mr-1" />
                  {fieldErrors.gradoAcademico}
                </p>
              )}
            </div>

            {/* Años de Experiencia */}
            <div>
              <label htmlFor="añosExperiencia" className="block text-sm font-medium text-gray-700 mb-2">
                Años de Experiencia Requeridos *
              </label>
              <input
                type="number"
                id="añosExperiencia"
                name="añosExperiencia"
                value={formData.añosExperiencia}
                onChange={handleChange}
                min={0}
                max={50}
                required
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 transition ${
                  fieldErrors.añosExperiencia
                    ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                    : 'border-gray-300 focus:ring-blue-500 focus:border-transparent'
                }`}
                placeholder="Ej: 5"
              />
              {fieldErrors.añosExperiencia && (
                <p className="mt-1 text-sm text-red-600 flex items-center">
                  <AlertCircle className="w-4 h-4 mr-1" />
                  {fieldErrors.añosExperiencia}
                </p>
              )}
            </div>
          </div>

          {/* Certificaciones Requeridas */}
          <div className="mt-6">
            <label htmlFor="certificacionesRequeridas" className="block text-sm font-medium text-gray-700 mb-2">
              Certificaciones Requeridas
            </label>
            <input
              type="text"
              id="certificacionesRequeridas"
              name="certificacionesRequeridas"
              value={formData.certificacionesRequeridas || ''}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Ej: AWS, Scrum Master, PMP (separar con comas)"
            />
            <p className="mt-1 text-xs text-gray-500">
              Opcional. Separar múltiples certificaciones con comas.
            </p>
          </div>

          {/* Otras Certificaciones */}
          <div className="mt-6">
            <label htmlFor="otrasCertificaciones" className="block text-sm font-medium text-gray-700 mb-2">
              Otras Certificaciones
            </label>
            <textarea
              id="otrasCertificaciones"
              name="otrasCertificaciones"
              value={formData.otrasCertificaciones || ''}
              onChange={handleChange}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Certificaciones adicionales no listadas arriba"
            />
          </div>

          {/* Idioma y paquetería (RH-02) */}
          <div className="mt-6">
            <label htmlFor="englishLevel" className="block text-sm font-medium text-gray-700 mb-2">
              Nivel de inglés requerido
            </label>
            <SelectBuscable
              variante="formulario"
              id="englishLevel"
              name="englishLevel"
              className="max-w-xs"
              value={formData.englishLevel ?? ''}
              onChange={(valor) =>
                setFormData((prev) => ({
                  ...prev,
                  englishLevel: (valor || null) as typeof prev.englishLevel,
                }))
              }
              placeholderDeBusqueda="Buscar nivel…"
              opciones={[{ value: '', label: 'No requerido' }, ...NIVELES_DE_INGLES]}
            />
          </div>

          <div className="mt-6">
            <PaqueteriaRequerida
              filas={formData.requiredSoftware ?? []}
              onChange={(filas) =>
                setFormData((prev) => ({ ...prev, requiredSoftware: filas }))
              }
            />
          </div>
        </div>

        {/*
          Sección: Candidatos Internos (RH-01). Va aquí, después del perfil:
          primero se define qué se busca y enseguida quién de la casa podría
          cubrirlo, antes de las condiciones económicas que solo ve RH.
        */}
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 border-b border-gray-200 pb-2">
            Candidatos internos
          </h2>
          <p className="text-sm text-gray-500 mb-4">
            Gente de la empresa que podría cubrir la vacante. La estrella marca
            al seleccionado, y se puede dejar sin marcar hasta que haya
            decisión.
          </p>
          <CandidatosInternos
            filas={formData.internalCandidates ?? []}
            onChange={(filas) =>
              setFormData((prev) => ({ ...prev, internalCandidates: filas }))
            }
          />
          {fieldErrors.internalCandidates && (
            <p className="mt-3 text-sm text-red-600 flex items-center">
              <AlertCircle className="w-4 h-4 mr-1" />
              {fieldErrors.internalCandidates}
            </p>
          )}
        </div>

        {/* 🔄 ACTUALIZADO: 29 Oct 2025 - Sección 4: Condiciones Económicas - Visible para ADMIN_RH */}
        {isAdminOrRH && (
          <div className="mb-8">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 border-b border-gray-200 pb-2">
              Condiciones Económicas
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Salario Mínimo */}
              <div>
                <label htmlFor="salarioMinimo" className="block text-sm font-medium text-gray-700 mb-2">
                  Salario Mínimo (MXN)
                </label>
                <input
                  type="number"
                  id="salarioMinimo"
                  name="salarioMinimo"
                  value={formData.salarioMinimo || ''}
                  onChange={handleChange}
                  min="0"
                  step="0.01"
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 transition ${
                    fieldErrors.salarioMinimo
                      ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                      : 'border-gray-300 focus:ring-blue-500 focus:border-transparent'
                  }`}
                  placeholder="ej. 45000.00"
                />
                {fieldErrors.salarioMinimo && (
                  <p className="mt-1 text-sm text-red-600 flex items-center">
                    <AlertCircle className="w-4 h-4 mr-1" />
                    {fieldErrors.salarioMinimo}
                  </p>
                )}
              </div>

              {/* Salario Máximo */}
              <div>
                <label htmlFor="salarioMaximo" className="block text-sm font-medium text-gray-700 mb-2">
                  Salario Máximo (MXN)
                </label>
                <input
                  type="number"
                  id="salarioMaximo"
                  name="salarioMaximo"
                  value={formData.salarioMaximo || ''}
                  onChange={handleChange}
                  min="0"
                  step="0.01"
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 transition ${
                    fieldErrors.salarioMaximo
                      ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                      : 'border-gray-300 focus:ring-blue-500 focus:border-transparent'
                  }`}
                  placeholder="ej. 65000.00"
                />
                {fieldErrors.salarioMaximo && (
                  <p className="mt-1 text-sm text-red-600 flex items-center">
                    <AlertCircle className="w-4 h-4 mr-1" />
                    {fieldErrors.salarioMaximo}
                  </p>
                )}
              </div>
            </div>

            {/* Prestaciones */}
            <div className="mt-6">
              <label htmlFor="prestaciones" className="block text-sm font-medium text-gray-700 mb-2">
                Prestaciones
              </label>
              <textarea
                id="prestaciones"
                name="prestaciones"
                value={formData.prestaciones}
                onChange={handleChange}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="ej. Seguro de gastos médicos mayores, seguro de vida, vales de despensa..."
              />
            </div>
          </div>
        )}

        {/* Sección 5: Detalles de la Solicitud */}
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 border-b border-gray-200 pb-2">
            Detalles de la Solicitud
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Motivo */}
            <div>
              <label htmlFor="motivo" className="block text-sm font-medium text-gray-700 mb-2">
                Motivo *
              </label>
              <SelectBuscable
                variante="formulario"
                id="motivo"
                name="motivo"
                value={formData.motivo}
                onChange={(valor) => handleChange(comoEventoDeCambio('motivo', valor))}
                required
                opciones={MOTIVOS_DE_SOLICITUD}
                placeholderDeBusqueda="Buscar motivo…"
              />
            </div>

            {/* Solo tiene sentido en un reemplazo, así que solo ahí aparece. */}
            {formData.motivo === 'reemplazo' && (
              <div>
                <label
                  htmlFor="previousEmployeeName"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Empleado anterior *
                </label>
                <input
                  type="text"
                  id="previousEmployeeName"
                  name="previousEmployeeName"
                  value={formData.previousEmployeeName ?? ''}
                  onChange={handleChange}
                  maxLength={255}
                  placeholder="Nombre de quien deja la posición"
                  aria-invalid={Boolean(fieldErrors.previousEmployeeName)}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:border-transparent ${
                    fieldErrors.previousEmployeeName
                      ? 'border-red-400 focus:ring-red-400'
                      : 'border-gray-300 focus:ring-blue-500'
                  }`}
                />
                {fieldErrors.previousEmployeeName && (
                  <p className="text-xs text-red-600 mt-1">
                    {fieldErrors.previousEmployeeName}
                  </p>
                )}
              </div>
            )}

            {/* Fecha Requerida de Inicio */}
            <div>
              <label htmlFor="fechaRequeridaInicio" className="block text-sm font-medium text-gray-700 mb-2">
                Fecha Requerida de Inicio *
              </label>
              <input
                type="date"
                id="fechaRequeridaInicio"
                name="fechaRequeridaInicio"
                value={formData.fechaRequeridaInicio}
                onChange={handleChange}
                required
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 transition ${
                  fieldErrors.fechaRequeridaInicio
                    ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                    : 'border-gray-300 focus:ring-blue-500 focus:border-transparent'
                }`}
              />
              {fieldErrors.fechaRequeridaInicio && (
                <p className="mt-1 text-sm text-red-600 flex items-center">
                  <AlertCircle className="w-4 h-4 mr-1" />
                  {fieldErrors.fechaRequeridaInicio}
                </p>
              )}
            </div>

            {/* Prioridad */}
            <div>
              <label htmlFor="prioridad" className="block text-sm font-medium text-gray-700 mb-2">
                Prioridad *
              </label>
              <SelectBuscable
                variante="formulario"
                id="prioridad"
                name="prioridad"
                value={formData.prioridad}
                onChange={(valor) => handleChange(comoEventoDeCambio('prioridad', valor))}
                required
                placeholderDeBusqueda="Buscar prioridad…"
                opciones={[
                  { value: 'baja', label: 'Baja' },
                  { value: 'media', label: 'Media' },
                  { value: 'alta', label: 'Alta' },
                  { value: 'urgente', label: 'Urgente' },
                ]}
              />
            </div>

            {/* Número de Vacantes */}
            <div>
              <label htmlFor="numeroPlazas" className="block text-sm font-medium text-gray-700 mb-2">
                Número de Vacantes
              </label>
              <input
                type="number"
                id="numeroPlazas"
                name="numeroPlazas"
                value={formData.numeroPlazas || 1}
                onChange={handleChange}
                onBlur={(e) => {
                  // El error se dice al salir del campo, no hasta intentar
                  // enviar: con 99 no pasaba nada hasta pulsar Crear.
                  const aviso = errorDeVacantes(e.currentTarget.valueAsNumber)
                  setFieldErrors((prev) => {
                    const { numeroPlazas: _anterior, ...resto } = prev
                    return aviso ? { ...resto, numeroPlazas: aviso } : resto
                  })
                }}
                onInvalid={e => {
                  // El form es nativo: sin esto Chrome frena el envio con su
                  // propio texto, en el idioma del navegador.
                  const input = e.currentTarget
                  input.setCustomValidity('')
                  input.setCustomValidity(avisoNativoDeVacantes(input.validity))
                }}
                min={MINIMO_DE_VACANTES}
                max={MAXIMO_DE_VACANTES}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 transition ${
                  fieldErrors.numeroPlazas
                    ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                    : 'border-gray-300 focus:ring-blue-500 focus:border-transparent'
                }`}
              />
              {fieldErrors.numeroPlazas && (
                <p className="mt-1 text-sm text-red-600 flex items-center">
                  <AlertCircle className="w-4 h-4 mr-1" />
                  {fieldErrors.numeroPlazas}
                </p>
              )}
            </div>
          </div>

          {/*
            Las tres en fila y no apiladas: son respuestas cortas del mismo
            tipo y en lista se leian como tres apartados sueltos. Con
            flex-wrap caen a renglon nuevo solas cuando no alcanza el ancho.
          */}
          <div className="mt-6 flex flex-wrap items-center gap-x-8 gap-y-3">
            <label className="flex items-center">
              <Checkbox
                name="entrevistaFinalSolicitante"
                checked={formData.entrevistaFinalSolicitante || false}
                onChange={handleChange} className="mr-2"
              />
              <span className="text-sm font-medium text-gray-700">
                Requiere segunda entrevista
              </span>
            </label>

            <label className="flex items-center">
              <Checkbox
                name="requiresHardwareSoftware"
                checked={formData.requiresHardwareSoftware || false}
                onChange={handleChange} className="mr-2"
              />
              <span className="text-sm font-medium text-gray-700">
                Requiere hardware o software
              </span>
            </label>

            <label className="flex items-center">
              <Checkbox
                name="requiresWorkstation"
                checked={formData.requiresWorkstation || false}
                onChange={handleChange} className="mr-2"
              />
              <span className="text-sm font-medium text-gray-700">
                Requiere equipo y escritorio
              </span>
            </label>
          </div>
        </div>

        {/* Sección 6: Información Adicional */}
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 border-b border-gray-200 pb-2">
            Información Adicional
          </h2>
          
          {/* Justificación de la Necesidad - ⚠️ AHORA OBLIGATORIO: 30 Oct 2025 */}
          <div className="mb-6">
            <label htmlFor="justificacionNecesidad" className="block text-sm font-medium text-gray-700 mb-2">
              Justificación de la Necesidad *
            </label>
            <TextareaQueCrece
              id="justificacionNecesidad"
              name="justificacionNecesidad"
              value={formData.justificacionNecesidad}
              onChange={handleChange}
              filasMinimas={4}
              required
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 transition ${
                fieldErrors.justificacionNecesidad
                  ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                  : 'border-gray-300 focus:ring-blue-500 focus:border-transparent'
              }`}
              placeholder="Describe por qué es necesaria esta posición (mínimo 10 caracteres)..."
            />
            {fieldErrors.justificacionNecesidad && (
              <p className="mt-1 text-sm text-red-600 flex items-center">
                <AlertCircle className="w-4 h-4 mr-1" />
                {fieldErrors.justificacionNecesidad}
              </p>
            )}
          </div>

          {/* Otra Información */}
          <div>
            <label htmlFor="otraInformacion" className="block text-sm font-medium text-gray-700 mb-2">
              Otra Información
            </label>
            <TextareaQueCrece
              id="otraInformacion"
              name="otraInformacion"
              value={formData.otraInformacion}
              onChange={handleChange}
              filasMinimas={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Información adicional relevante..."
            />
          </div>
        </div>

        {/* Alerta de validación general */}
        {showValidationAlert && Object.keys(fieldErrors).length > 0 && (
          <div className="mb-6 bg-amber-50 border-l-4 border-amber-400 p-4 rounded-r-lg">
            <div className="flex items-start">
              <AlertCircle className="w-5 h-5 text-amber-500 mr-3 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-amber-800 mb-1">
                  Por favor, revise los siguientes campos:
                </h3>
                <ul className="text-sm text-amber-700 list-disc list-inside space-y-1">
                  {Object.entries(fieldErrors).map(([field, error]) => (
                    <li key={field}>{error}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Submit Button */}
        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={() => navigate(-1 as any)}
            className={estiloDeBoton('secundario')}
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={isLoading || avisoDeLosPuestos === 'sin_departamento' || avisoDeLosPuestos === 'sin_puestos'}
            className={estiloDeBoton('primario')}
          >
            {isLoading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Creando...
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                Crear Solicitud
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  )
}

export default CreateJobRequest
